import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, CurrencyCircleDollar } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { LoanLedgerSummary } from "../components/LoanLedgerSummary";
import { LoanPaymentTimeline } from "../components/LoanPaymentTimeline";
import RecordPaymentDialog from "../components/RecordPaymentDialog";
import EditPaymentDialog from "../components/EditPaymentDialog";
import DeletePaymentDialog from "../components/DeletePaymentDialog";
import DeleteSettlementDialog from "../components/DeleteSettlementDialog";
import EditSettlementDialog from "../components/EditSettlementDialog";
import api, { formatApiError } from "../lib/api";

const displayDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

export default function LoanLedgerWorkspaceV4() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [ledger, setLedger] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editingSettlement, setEditingSettlement] = useState(null);
  const [deletingSettlement, setDeletingSettlement] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ledgerResponse, userResponse] = await Promise.all([
        api.get(`/loans/${loanId}/repayment-ledger`),
        api.get("/auth/me"),
      ]);
      setLedger(ledgerResponse.data);
      setUser(userResponse.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [loanId]);

  const ledgerView = useMemo(() => {
    if (!ledger || !user) {
      return null;
    }
    return {
      canEditPayments: ["admin", "master"].includes(user.role),
      canPay: ledger.loan.status !== "paid" && ledger.loan.outstanding > 0,
      loan: ledger.loan,
      payments: ledger.payments,
      summary: ledger.summary,
    };
  }, [ledger, user]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="page ledger-page" data-testid="loan-ledger-error">
        <button
          className="ledger-back-button"
          onClick={() => navigate(-1)}
          data-testid="loan-ledger-error-back-button"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="empty-state" data-testid="loan-ledger-error-message">{error}</div>
      </div>
    );
  }

  if (!ledgerView) {
    return <div className="page-loading" data-testid="loan-ledger-loading">Loading loan record…</div>;
  }

  return (
    <div className="page ledger-page" data-testid="loan-ledger-page">
      <button
        className="ledger-back-button"
        onClick={() => navigate(-1)}
        data-testid="loan-ledger-back-button"
      >
        <ArrowLeft size={16} /> Back to customer record
      </button>
      <header className="page-header">
        <div>
          <h1>Loan {ledgerView.loan.id.slice(-6).toUpperCase()}</h1>
          <p>{ledgerView.loan.borrower_name} · permanent repayment record</p>
        </div>
        {ledgerView.canPay && (
          <Button onClick={() => setShowPayment(true)} data-testid="loan-ledger-pay-button">
            <CurrencyCircleDollar size={17} /> Pay
          </Button>
        )}
      </header>
      <section className="ledger-page-meta">
        <span>
          Created: <strong>{ledgerView.loan.loan_date || displayDate(ledgerView.loan.created_at)}</strong>
        </span>
        <span>
          Term: <strong>{ledgerView.loan.term_days ? `${ledgerView.loan.term_days} days` : `${ledgerView.loan.term_months} months`}</strong>
        </span>
        <span>
          Next Due: <strong data-testid="next-due-date-label">{ledgerView.loan.next_due_date}</strong>
        </span>
        <span>
          Remarks: <strong>{ledgerView.loan.remarks || "—"}</strong>
        </span>
      </section>
      <LoanLedgerSummary loan={ledgerView.loan} summary={ledgerView.summary} />
      <LoanPaymentTimeline
        canEdit={ledgerView.canEditPayments}
        onDelete={setDeleting}
        onDeleteSettlement={setDeletingSettlement}
        onEdit={setEditing}
        onEditSettlement={setEditingSettlement}
        payments={ledgerView.payments}
      />
      <RecordPaymentDialog
        ledger={ledger}
        open={showPayment}
        onClose={() => setShowPayment(false)}
        onDone={load}
      />
      <EditPaymentDialog
        loanId={ledgerView.loan.id}
        payment={editing}
        onClose={() => setEditing(null)}
        onDone={load}
      />
      <DeletePaymentDialog
        payment={deleting}
        onClose={() => setDeleting(null)}
        onDone={load}
      />
      <EditSettlementDialog
        loanId={ledgerView.loan.id}
        settlement={editingSettlement}
        onClose={() => setEditingSettlement(null)}
        onDone={load}
      />
      <DeleteSettlementDialog
        loanId={ledgerView.loan.id}
        settlement={deletingSettlement}
        onClose={() => setDeletingSettlement(null)}
        onDone={load}
      />
    </div>
  );
}